"use client";

/**
 * Fullscreen preview for images embedded in notes. Opened by clicking an
 * image in any rendered note (notes tab, activity timeline, notes popover).
 * Closes on backdrop click, X, or Escape. "Open original" opens the source
 * file in a new tab for full resolution / download.
 */

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ExternalLink, Loader2 } from "lucide-react";

export function NoteImageLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  const [openingOriginal, setOpeningOriginal] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Open full-size via a blob: URL on OUR origin — never puts the storage
  // domain (or the uploader-id file path) in the user's address bar.
  const openOriginal = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (openingOriginal) return;
    setOpeningOriginal(true);
    // Open the tab SYNCHRONOUSLY (inside the click's user gesture — opening
    // after the await gets popup-blocked), then point it at the blob.
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    try {
      const res = await fetch(src);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      if (win) win.location.href = blobUrl;
      // Revoke later — the new tab needs the URL alive while it loads.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch {
      // Never fall back to the raw storage URL — just close the empty tab.
      win?.close();
    } finally {
      setOpeningOriginal(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 p-6"
      onClick={onClose}
      role="dialog"
      aria-label="Image preview"
    >
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <button
          type="button"
          onClick={openOriginal}
          disabled={openingOriginal}
          className="flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20 transition-colors disabled:opacity-60"
        >
          {openingOriginal ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />} Open original
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md bg-white/10 p-1.5 text-white hover:bg-white/20 transition-colors"
          title="Close (Esc)"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt="Note image preview"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
      />
    </div>,
    document.body,
  );
}

/**
 * Shared click-delegation helper: returns the storage-hosted image URL when a
 * click inside a rendered note landed on an embedded image, else null.
 */
export function noteImageFromClick(e: React.MouseEvent): string | null {
  const t = e.target as HTMLElement;
  if (t instanceof HTMLImageElement && t.src) {
    e.preventDefault();
    e.stopPropagation();
    return t.src;
  }
  return null;
}
